/** Mirrors `pwa/lib/api.ts` — keep human-readable messages for structured FastAPI errors. */

export function resolveApiErrorDetail(errorData: Record<string, unknown> | undefined): unknown {
  if (!errorData) return undefined;
  if ('detail' in errorData && errorData.detail != null) return errorData.detail;
  if ('error' in errorData && errorData.error != null) return errorData.error;
  return undefined;
}

function resolveApiErrorFallbackString(fallback: unknown): string {
  if (typeof fallback === 'string' && fallback.trim()) return fallback;
  if (fallback == null) return 'Ошибка запроса';
  if (typeof fallback === 'number' || typeof fallback === 'boolean') return String(fallback);
  if (typeof fallback === 'object') {
    const o = fallback as Record<string, unknown>;
    if (typeof o.message === 'string' && o.message.trim()) return o.message;
    if (typeof o.msg === 'string' && o.msg.trim()) return o.msg;
    if (typeof o.detail === 'string' && o.detail.trim()) return o.detail;
    if (typeof o.error === 'string' && o.error.trim()) return o.error;
  }
  return 'Ошибка запроса';
}

const API_ERROR_CODE_TO_MESSAGE: Record<string, string> = {
  too_many_attempts:
    'Слишком много попыток. Подождите и повторите. Если аккаунта ещё нет — обратитесь к администратору.',
  submit_report_only_for_owner_shift: 'Можно отправлять отчёт только по своей смене.',
  submit_report_requires_closed_shift:
    'Можно отправить отчёт только по закрытой смене. Сначала закройте смену.',
  instance_completed: 'Слот уже завершён.',
  not_your_instance: 'Слот взят другим сотрудником.',
  instance_not_found: 'Слот расписания не найден.',
  shift_close_required_step_missing: 'Не выполнены обязательные шаги перед закрытием смены.',
  employee_has_open_shift: 'У вас уже открыта смена. Сначала закройте её.',
  closing_skip_reason_required: 'Укажите причину закрытия без чек-листа.',
  upload_content_type_mismatch:
    'Содержимое файла не соответствует формату. Выберите фото JPEG или PNG и попробуйте снова.',
  reset_url_not_configured:
    'На сервере не настроен адрес приложения для ссылки сброса пароля. Обратитесь к поддержке.',
  account_inactive:
    'Учётная запись сотрудника отключена. Включите доступ или обратитесь к администратору.',
};

const LEGACY_ENGLISH_API_MESSAGES: Record<string, string> = {
  'Can only submit reports for your own shift':
    API_ERROR_CODE_TO_MESSAGE.submit_report_only_for_owner_shift,
  'Can only submit report for a closed shift':
    API_ERROR_CODE_TO_MESSAGE.submit_report_requires_closed_shift,
  'Failed to release instance':
    'Не удалось вернуть слот в пул. Попробуйте позже или обратитесь к менеджеру.',
  'File content does not match declared type':
    API_ERROR_CODE_TO_MESSAGE.upload_content_type_mismatch,
};

export function formatApiErrorDetail(detail: unknown, fallback: unknown): string {
  const fb = resolveApiErrorFallbackString(fallback);

  if (typeof detail === 'string' && detail.trim()) {
    const trimmed = detail.trim();
    const legacy = LEGACY_ENGLISH_API_MESSAGES[trimmed];
    if (legacy) return legacy;
    if (API_ERROR_CODE_TO_MESSAGE[trimmed]) return API_ERROR_CODE_TO_MESSAGE[trimmed];
    return trimmed;
  }

  if (Array.isArray(detail)) {
    const parts: string[] = [];
    for (const item of detail) {
      if (typeof item === 'string' && item.trim()) {
        parts.push(item.trim());
        continue;
      }
      if (item && typeof item === 'object') {
        const it = item as Record<string, unknown>;
        const m =
          typeof it.msg === 'string'
            ? it.msg
            : typeof it.message === 'string'
              ? it.message
              : null;
        if (m?.trim()) parts.push(m.trim());
      }
    }
    if (parts.length) return parts.join('; ');
  }

  if (detail && typeof detail === 'object') {
    const d = detail as Record<string, unknown>;
    const codeStr =
      typeof d.code === 'string' && d.code.trim() ? d.code.trim() : null;
    if (codeStr && API_ERROR_CODE_TO_MESSAGE[codeStr]) {
      return API_ERROR_CODE_TO_MESSAGE[codeStr];
    }
    if (typeof d.message === 'string' && d.message.trim()) return d.message;
    if (typeof d.msg === 'string' && d.msg.trim()) return d.msg;

    const codeField = typeof d.code === 'string' && d.code.trim() ? d.code.trim() : null;
    const nestedDetail = typeof d.detail === 'string' ? d.detail.trim() : null;
    const closeStepCode =
      codeField === 'shift_close_required_step_missing' ||
      nestedDetail === 'shift_close_required_step_missing'
        ? 'shift_close_required_step_missing'
        : null;

    if (closeStepCode) {
      const steps = d.step_descriptions;
      const ms = d.missing_steps;
      if (
        steps &&
        typeof steps === 'object' &&
        !Array.isArray(steps) &&
        Array.isArray(ms) &&
        ms.length
      ) {
        const labels: string[] = [];
        for (const k of ms) {
          const desc = (steps as Record<string, unknown>)[String(k)];
          if (typeof desc === 'string' && desc.trim()) labels.push(desc.trim());
        }
        if (labels.length) return labels.join('. ');
      }
      return 'Не выполнены обязательные шаги перед закрытием смены.';
    }

    if (codeField === 'employee_has_open_shift') {
      const loc =
        typeof d.location_name === 'string' && d.location_name.trim()
          ? ` на точке «${d.location_name.trim()}»`
          : '';
      return `У вас уже открыта смена${loc}. Сначала закройте её.`;
    }

    if (nestedDetail && !closeStepCode) {
      const leg = LEGACY_ENGLISH_API_MESSAGES[nestedDetail];
      if (leg) return leg;
      return nestedDetail;
    }
    if (codeField && !closeStepCode) {
      if (API_ERROR_CODE_TO_MESSAGE[codeField]) return API_ERROR_CODE_TO_MESSAGE[codeField];
      return codeField;
    }
  }

  return fb;
}

/** Browser network errors (CSP block, offline) — human-readable Russian. */
export function formatNetworkFetchError(message: unknown, fallback = 'Не удалось выполнить запрос'): string {
  const raw = typeof message === 'string' ? message.trim() : '';
  if (!raw) return fallback;
  const lower = raw.toLowerCase();
  if (
    lower.includes('failed to fetch') ||
    lower.includes('networkerror') ||
    lower.includes('load failed') ||
    lower.includes('network request failed')
  ) {
    return 'Не удалось связаться с сервером. Проверьте интернет или обновите страницу.';
  }
  return raw;
}
